import React from 'react';
import { Components } from 'react-markdown';
import { MaterialSymbol } from 'react-material-symbols'; // Adjust this import as needed

// TableWithDelete component props
interface TableWithDeleteProps extends React.TableHTMLAttributes<HTMLTableElement> {
  onDelete?: (id: string | number, rowIndex: number) => void;
}

const TableWithDelete = ({ children, onDelete, ...props }: TableWithDeleteProps) => {
  return (
    <table className="w-full border-collapse text-sm text-dark-default-content" {...props}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === 'thead') {
          const headerRows = React.Children.toArray(child.props.children);
          return (
            <thead className="bg-dark-subdued-stroke text-left text-xs font-semibold uppercase tracking-wide">
              {headerRows.map((headerRow, i) => {
                if (React.isValidElement(headerRow)) {
                  return React.cloneElement(
                    headerRow,
                    { key: i },
                    <>
                      {React.Children.toArray(headerRow.props.children)}
                      <th key="action-header" className="px-6 py-3 text-dark-default-content">
                        {' '}
                      </th>
                    </>,
                  );
                }
                return headerRow;
              })}
            </thead>
          );
        }

        if (React.isValidElement(child) && child.type === 'tbody') {
          const tbodyProps = child.props as { children?: React.ReactNode };
          const bodyRows = React.Children.toArray(tbodyProps.children);

          return (
            <tbody>
              {bodyRows.map((row, rowIndex) => {
                if (React.isValidElement(row)) {
                  const rowProps = row.props as { children?: React.ReactNode };
                  const cells = React.Children.toArray(rowProps.children);
                  const firstCell = cells[0];
                  const id = React.isValidElement(firstCell) ? firstCell.props.children : rowIndex;

                  return React.cloneElement(
                    row,
                    { key: rowIndex },
                    <>
                      {cells}
                      <td className="px-6 py-4 border-b border-stroke-on-dark-subdued text-center">
                        <button
                          onClick={() => {
                            onDelete && onDelete(id, rowIndex);
                          }}
                          className="p-1 text-red-500 hover:text-red-700 rounded focus:outline-none"
                          aria-label="Delete"
                        >
                          <MaterialSymbol icon="delete" />
                        </button>
                      </td>
                    </>,
                  );
                }
                return row;
              })}
            </tbody>
          );
        }

        return child;
      })}
    </table>
  );
};

// The factory function returns an object that partially implements Components.
export const createMarkdownTableWithExtraColumn = (
  onDelete: (id: string | number, rowIndex: number) => void,
): Partial<Components> => {
  const { thead: _thread, ...restMarkdown } = MarkdownComponents;
  return {
    ...restMarkdown,
    table: (props) => <TableWithDelete {...props} onDelete={onDelete} />,
  };
};

// Now we type each markdown component explicitly so they match react-markdown’s Components type.
export const MarkdownComponents: Components = {
  h1: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h1 className="text-xl font-semibold mt-4 mb-3" {...props}>
      {children}
    </h1>
  ),
  h2: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h2 className="text-lg font-semibold mt-3 mb-2" {...props}>
      {children}
    </h2>
  ),
  h3: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h3 className="text-base font-semibold mt-3 mb-2" {...props}>
      {children}
    </h3>
  ),
  h4: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h4 className="text-sm font-semibold mt-2 mb-1" {...props}>
      {children}
    </h4>
  ),
  h5: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h5 className="text-sm font-medium mt-2 mb-1" {...props}>
      {children}
    </h5>
  ),
  h6: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLHeadingElement> & { children?: React.ReactNode }) => (
    <h6 className="text-xs font-medium mt-2 mb-1" {...props}>
      {children}
    </h6>
  ),

  // Links
  a: ({
    children,
    ...props
  }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => (
    <a className="text-cyan-400 underline hover:no-underline" {...props}>
      {children}
    </a>
  ),

  // Code blocks
  code: ({
    inline,
    className,
    children,
    ...props
  }: {
    inline?: boolean;
    className?: string;
    children?: React.ReactNode;
  } & React.HTMLAttributes<HTMLElement>) => {
    if (inline) {
      return (
        <code
          className="bg-gray-800 text-gray-200 px-1 py-0.5 rounded text-xs font-mono"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={`block bg-gray-800 text-gray-200 p-3 rounded font-mono text-xs overflow-x-auto ${className || ''}`}
        {...props}
      >
        {children}
      </code>
    );
  },

  pre: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLPreElement> & { children?: React.ReactNode }) => (
    <pre className="bg-gray-800 rounded p-3 my-3 overflow-x-auto" {...props}>
      {children}
    </pre>
  ),

  // Blockquotes
  blockquote: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => (
    <blockquote className="border-l-4 border-cyan-400 pl-3 italic text-gray-400 my-3" {...props}>
      {children}
    </blockquote>
  ),

  // Horizontal rule
  hr: ({ ...props }: React.HTMLAttributes<HTMLHRElement>) => (
    <hr className="border-t border-gray-700 my-4" {...props} />
  ),

  // Images
  img: ({ ...props }: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img className="max-w-full h-auto rounded my-3" {...props} />
  ),

  // Tables
  table: ({
    children,
    ...props
  }: React.TableHTMLAttributes<HTMLTableElement> & { children?: React.ReactNode }) => (
    <table className="w-full border-collapse text-sm text-dark-default-content" {...props}>
      {children}
    </table>
  ),

  thead: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLTableSectionElement> & { children?: React.ReactNode }) => (
    <thead
      className="bg-dark-subdued-stroke text-left text-xs font-semibold uppercase tracking-wide"
      {...props}
    >
      {children}
    </thead>
  ),

  tr: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLTableRowElement> & { children?: React.ReactNode }) => (
    <tr className="hover:bg-dark-hover transition-colors" {...props}>
      {children}
    </tr>
  ),

  th: ({
    children,
    ...props
  }: React.ThHTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode }) => (
    <th className="px-6 py-3 text-dark-default-content" {...props}>
      {children}
    </th>
  ),

  td: ({
    children,
    ...props
  }: React.TdHTMLAttributes<HTMLTableCellElement> & { children?: React.ReactNode }) => (
    <td className="px-6 py-4 border-b border-stroke-on-dark-subdued" {...props}>
      {children}
    </td>
  ),

  // Lists
  ul: ({ ...props }: React.HTMLAttributes<HTMLUListElement>) => (
    <ul
      className="list-disc list-outside pl-6 leading-tight"
      {...props}
      style={{
        lineHeight: '0.5rem',
      }}
    />
  ),

  ol: ({ ...props }: React.OlHTMLAttributes<HTMLOListElement>) => (
    <ol
      className="list-decimal list-outside pl-6 leading-tight"
      {...props}
      style={{
        lineHeight: '0.5rem',
      }}
    />
  ),

  li: ({ ...props }: React.LiHTMLAttributes<HTMLLIElement>) => (
    <li className="leading-relaxed" {...props} />
  ),

  // Paragraphs
  p: ({ ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
    <p className="leading-relaxed" {...props} />
  ),

  // Strong/bold
  strong: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => (
    <strong className="font-bold" {...props}>
      {children}
    </strong>
  ),

  // Emphasis/italic
  em: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),

  // Deleted text
  del: ({
    children,
    ...props
  }: React.HTMLAttributes<HTMLElement> & { children?: React.ReactNode }) => (
    <del className="line-through" {...props}>
      {children}
    </del>
  ),
};
